import { Box } from "@mui/material"
import clsx from "clsx"
import React, { memo, useEffect, useMemo, useRef } from "react"
import { useCallback, useState } from "react"
import ResizeObserverPoly from "resize-observer-polyfill"
import empty from "src/components/images/empty.png"
import { CuratedContentPageQuery } from "src/generated/graphql"
import useTranslate from "src/hooks/use-translate"
import { theme, TI } from "src/utilities/theme"

const ro =
  typeof ResizeObserver === "undefined" ? ResizeObserverPoly : ResizeObserver
import { Reruns } from "../../screens/Detail/components/Reruns"
import { ArrayElement } from "../../types"
import { s } from "../../utilities/common"
import { deviceResolution, nonStandardFormat } from "../../utilities/constants"
import { ContentLabels } from "../common/ContentLabels"
import { DGText } from "../common/DGText"
import { Image } from "../common/Image"
import { LiveConcertTime } from "../common/LiveConcertTime"
import { Label } from "../Sliders/Teaser"

import { LiveConcertCountdown } from "./LiveConcertCountdown"
import { useBanner } from "./use-banner"
import "./Banner.css"

export const Banner = memo(
  ({
    data,
  }: {
    data: ArrayElement<
      CuratedContentPageQuery["curatedContentPage"]["stageSlider"]
    >
  }) => {
    const { image, albumImage, title, subtitle } = useBanner(data)
    const textboxes = useRef<HTMLElement>(null)
    const runOnce = useRef(true)
    const t = useTranslate()
    // prevent text adjust on screen
    useEffect(() => {
      if (runOnce.current) {
        if (textboxes.current)
          new ro(calculateSubLines).observe(textboxes.current)
        runOnce.current = false
      }
      calculateSubLines()
    }, [])
    const [subtitleLines, setSubtitleLines] = useState(1)

    const lockSubCalc = useRef(false)
    const calculateSubLines = useCallback(() => {
      // ellipsis calculation
      if (
        lockSubCalc.current ||
        !textboxes.current ||
        textboxes.current.getBoundingClientRect().height === 0
      )
        return
      setTimeout(() => {
        const height = data.item.__typename === "Album" ? s(336) : s(264)
        const check = !textboxes.current
          ? 0
          : textboxes.current.getBoundingClientRect().height < height
            ? 1
            : textboxes.current.getBoundingClientRect().height > height
              ? -1
              : 0
        if (check === -1) lockSubCalc.current = true
        setSubtitleLines((prevState) => Math.max(1, prevState + check))
      }, 50)
    }, [subtitleLines, textboxes, data])

    const liveConcert =
      data.item.__typename === "LiveConcert" ? data.item : undefined
    const vodConcert =
      data.item.__typename === "VodConcert" ? data.item : undefined

    const publicationLevel = useMemo(() => {
      return data.item.__typename === "LiveConcert" ||
        data.item.__typename === "VodConcert" ||
        data.item.__typename === "Video"
        ? data.item.publicationLevel
        : "TICKET_REQUIRED"
    }, [data])

    return (
      <Box sx={{ display: "flex", flexDirection: "column", pl: s("96px") }}>
        <Box
          className={clsx(
            "BannerBackground",
            deviceResolution === 720 && "small",
            nonStandardFormat && "dynamic",
          )}
          sx={{
            width: s("1728px"),
            height: s("558px"),
            float: "left",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {liveConcert && (
            <Box
              sx={{
                position: "absolute",
                top: s("48px"),
                left: s("889.6px"),
                zIndex: 2,
              }}
            >
              <LiveConcertCountdown liveConcert={liveConcert} />
            </Box>
          )}
          <Image
            className={clsx(
              "BannerImage",
              deviceResolution === 720 && "small",
              nonStandardFormat && "dynamic",
            )}
            style={{ backgroundClip: "content-box" }}
            sx={{
              position: "absolute",
              backgroundRepeat: "no-repeat",
              backgroundImage: `url(${empty})`,
              backgroundPosition: "center",
              backgroundSize: s("360px"),
            }}
            hasTransparentFallback
            src={image}
          />
          {(data.item.__typename === "Album" ||
            data.item.__typename === "EditorialPlaylist") && (
            <Image
              sx={{
                position: "absolute",
                height: s("405px"),
                width: s("405px"),
                top: s("68px"),
                left: s("165px"),
              }}
              src={albumImage}
              hasTransparentFallback
            />
          )}
          <Box
            className={clsx(
              "BannerOverlay1",
              deviceResolution === 720 && "small",
            )}
            style={{ backgroundClip: "content-box" }}
            sx={{
              height: "100%",
              width: s("992px"),
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
          <Box
            style={{ backgroundClip: "content-box" }}
            sx={{
              height: "100%",
              width: s("992px"),
              position: "absolute",
              top: 0,
              left: 0,
              background: "rgba(0, 0, 0, 0.15)",
            }}
          />
          <Box
            className={clsx(
              "BannerOverlay2",
              deviceResolution === 720 && "small",
            )}
            style={{ backgroundClip: "content-box" }}
            sx={{
              height: "100%",
              width: s("992px"),
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />

          <ContentLabels
            sx={{ position: "absolute", left: s("20px"), top: s("20px") }}
            publicationLevel={publicationLevel}
            content={liveConcert || vodConcert}
          />

          <Box
            sx={{
              // add more height for the playlist label
              height:
                data.item.__typename !== "EditorialPlaylist"
                  ? s("330px")
                  : s("370px"),
              width: data.item.__typename === "Album" ? s("946px") : s("846px"),
              position: "absolute",
              top:
                data.item.__typename !== "EditorialPlaylist"
                  ? s("142px")
                  : s("102px"),
              left: s("735px"),
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {data.item.__typename === "EditorialPlaylist" && (
              <Box sx={{ mb: s("10px") }}>
                <Label
                  color={theme.palette.white.main}
                  backgroundColor={theme.palette.primary.main}
                >
                  {t("api__type_media_playlist")}
                </Label>
              </Box>
            )}
            {liveConcert ? (
              <LiveConcertTime liveConcert={liveConcert} />
            ) : (
              <DGText
                tx={TI.Subheadline05tv}
                sx={{
                  color: theme.palette.tertiary.accentYellow,
                  textAlign: "center",
                }}
              >
                {data.label ? (
                  <span style={{ textTransform: "uppercase" }}>
                    {data.label}
                  </span>
                ) : (
                  // maintain height by inserting blank space
                  <span>&nbsp;</span>
                )}
              </DGText>
            )}
            <Box ref={textboxes}>
              <DGText
                sx={{
                  textAlign: "center",
                  color: theme.palette.white.main,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: data.item.__typename === "Album" ? 3 : 2,
                  fontVariantNumeric: "oldstyle-nums",
                  fontFeatureSettings: "onum",
                }}
                tx={TI.H03tv}
              >
                {title}
              </DGText>
              <DGText
                sx={{
                  textAlign: "center",
                  color: theme.palette.white.main,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: subtitleLines,
                  fontVariantNumeric: "oldstyle-nums",
                  fontFeatureSettings: "onum",
                }}
                tx={TI.H07tv}
              >
                {subtitle}
              </DGText>
              {liveConcert ? (
                <Box sx={{ mt: s("36px") }}>
                  <Reruns
                    singleLine
                    liveConcert={liveConcert}
                    sx={{ fontSize: s("30px"), textAlign: "center" }}
                  />
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
    )
  },
)
